/**
 * CRM Multi-Compagnia · ColumnMapper
 *
 * UI side-by-side: a sinistra colonne file SIA, a destra select target field CRM.
 */
import { useMemo } from "react";
import { Sparkles, AlertCircle } from "lucide-react";
import { Select, TextInput } from "@/components/crm-multi/shared";

export function ColumnMapper({ columns, targetFields, mapping, setMapping,
                               suggested, requiredFields = [], previewRows = [] }) {
  const mappedTargets = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = requiredFields.filter(f => !mappedTargets.has(f));

  const set = (col, target) => setMapping({ ...mapping, [col]: target || null });

  return (
    <div className="space-y-3">
      {missingRequired.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-800 flex items-start gap-2" data-testid="sia-mapping-missing">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Campi obbligatori non mappati:</strong> {missingRequired.join(", ")}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-2 bg-zinc-50 px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium">
          <div className="col-span-5">Colonna nel file SIA</div>
          <div className="col-span-3">Esempio</div>
          <div className="col-span-4">Campo CRM destinazione</div>
        </div>
        {columns.map((col, idx) => {
          const sample = previewRows[0]?.[col];
          const isSuggested = suggested?.[col] && mapping[col] === suggested[col];
          const isRequired = mapping[col] && requiredFields.includes(mapping[col]);
          return (
            <div key={col} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-zinc-100 items-center"
                 data-testid={`sia-mapping-row-${idx}`}>
              <div className="col-span-5">
                <p className="text-sm font-medium text-zinc-900">{col}</p>
                {suggested?.[col] && (
                  <p className="text-[10px] text-emerald-600 inline-flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> Suggerito: {suggested[col]}
                  </p>
                )}
              </div>
              <div className="col-span-3 text-xs text-zinc-500 truncate">{sample ?? "—"}</div>
              <div className="col-span-4 flex items-center gap-1">
                <Select
                  value={mapping[col] || ""}
                  onChange={(e) => set(col, e.target.value)}
                  data-testid={`sia-mapping-select-${idx}`}
                  className={isSuggested ? "border-emerald-400 bg-emerald-50/40" : ""}
                >
                  <option value="">— Ignora —</option>
                  {targetFields.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </Select>
                {isRequired && <span className="text-rose-500 text-xs" title="Campo obbligatorio">*</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
